import * as types from '@/store/mutation-types'
import actions from './sell-actions'
import {urls} from '@/api'
import {sellFormula, isRequiredAttribute} from '@/helpers'

const setInitialState = () => ({
  id: null,
  category_id: null,
  category_path: [],
  mla_category_id: '',
  publication_status_id: undefined,
  publication_status: undefined,
  title: '',
  body: '',
  locations: [],
  phones: [],
  emails: [],
  geopoint: null,
  price: {
    currency_code: 'ARS',
    currency_value: null
  },
  enable_questions: true,
  auto_republication: false,
  conditions: true,
  fetchedAttributes: [],
  images: [],
  imagesMaxSize: 1280,
  // it's better start with FREE because this controls some v-if
  type: 'FREE',
  frontpage: 1,
  highlightRequested: false,
  highlightType: ['GLD'],
  period_length: 3,
  period_unit: 'DAY',
  rotating_images: 0,
  band_code: '',
  band_text: '',
  band_color: '#05A429',
  payment_gateway: 'OFFICE',
  highlightsCalc: {},
  highlightsFinal: {},
  checkRequiredFields: false,
  fields_filled: false,
  attrWithErrors: [],
  stock: 1,
  forSale: 0,
  limit_on_category: undefined,
  remaining_publications: undefined,
  highlight_pending: false,
  catalogDomain: '',
  attributeSuggestions: [],
  apiError: false,
  isFetching: false,
  shipping_type: 'TO_AGREE',
  category_marketable: false
})

const state = setInitialState()

const getters = {
  required: (state) =>
    state.fetchedAttributes.filter(a => isRequiredAttribute(a)),

  /** @return {Number.integer} all required attributes */
  allRequired: (state, getters) =>
    getters.required.length + 2,

  notRequired: (state) =>
    state.fetchedAttributes.filter(a => !isRequiredAttribute(a)),

  /** @return {Number.integer} all not required attributes */
  allNotRequired: (state, getters) =>
    getters.notRequired.length,

  /** @return {Number.integer} all fixed attributes */
  fixedRequiredAttributes: (state, getters) =>
    getters.required.filter(a => a.mpv_fixed).length,

  /** @return {Number.integer} all fixed attributes */
  fixedNotRequiredAttributes: (state, getters) =>
    getters.notRequired.filter(a => a.mpv_fixed).length,

  /** @return {Number.integer} all required attributes set */
  filledRequired: (state, getters) =>
    getters.attributes.filter(a => isRequiredAttribute(a)).length +
      (state.title ? 1 : 0) + (state.body ? 1 : 0),
  /** @return {Number.integer} all not required attributes set */
  filledNotRequired: (state, getters) =>
    getters.attributes.filter(a => !isRequiredAttribute(a)).length,

  /** @return {Number.integer} 0 to 100 for the widget */
  visibility: (state, getters) => sellFormula({
    images: state.images.length,
    required: getters.filledRequired - getters.fixedRequiredAttributes,
    optionals: getters.filledNotRequired - getters.fixedNotRequiredAttributes,
    totalRequired: getters.allRequired - getters.fixedRequiredAttributes,
    totalOptionals: getters.allNotRequired - getters.fixedNotRequiredAttributes
  }),

  /** @return {Array[Object]} setted attributes ready for submit */
  attributes: state => state.fetchedAttributes
    .filter(a => (a.mpv_fixed && a.values.length === 1) || (a.setted_value || a.setted_value === 0))
    .map(a => ({
      mpv_required: isRequiredAttribute(a),
      id: a.id,
      setted_value_id: a.mpv_fixed ? a.values[0].id : a.setted_value_id,
      setted_value: a.mpv_fixed ? a.values[0].name : a.setted_value
    })),

  preview: (state, getters, rootState) => ({
    ready: !!state.title,
    highlight: state.highlightRequested && {
      highlight_band: {
        color: state.band_color,
        text: state.band_text,
        code: !!state.band_code
      },
      rotating_images: state.rotating_images,
      highlight_types: state.highlightType
    },
    date: '',
    category_path: state.category_path.map(({name}) => name).join('/'),
    media: state.images,
    title: state.title,
    currency: state.price && state.price.currency_code,
    price: state.price && state.price.currency_value,
    geopoint: state.geopoint
  }),

  highlightOrder: (state, getters) => ({
    ...getters.publicationOrder,
    highlight: {
      highlight_type_codes: state.highlightType.concat(state.frontpage ? ['HM'] : []),
      period_length: state.period_length,
      period_unit: 'DAY',
      rotating_images: state.rotating_images,
      band_code: state.band_code,
      band_color: state.band_color
    }
  }),

  cheapestHighlight: (state, getters) => ({
    ...getters.publicationOrder,
    highlight: {
      highlight_type_codes: ['SLVR'],
      period_length: 3,
      period_unit: 'DAY',
      rotating_images: 0,
      band_code: '',
      band_color: ''
    }
  }),

  publicationOrder: (state) => ({
    publication_id: state.id,
    payment_gateway: state.payment_gateway,
    mla_category_id: state.mla_category_id
  })
}

const mutations = {
  [types.RESET_SELL_INITIAL_STATE] (state) { Object.assign(state, setInitialState()) },

  [types.SET_SELL_ID] (state, id) { state.id = id },

  [types.SET_SELL_CATEGORY_ID] (state, id) { state.category_id = id },

  [types.SET_SELL_CATEGORY_PATH] (state, pathList) {
    state.category_path = pathList
  },

  [types.SET_SELL_MLA_CATEGORY_ID] (state, id) { state.mla_category_id = id },

  [types.SET_SELL_TITLE] (state, title) { state.title = title },

  [types.SET_SELL_BODY] (state, body) { state.body = body },

  [types.ADD_SELL_LOCATION] (state, loc) {
    if (!state.locations.find(l => l.id === loc.id)) {
      state.locations = [...state.locations, loc]
    }
  },

  [types.REMOVE_SELL_LOCATION] (state, loc) {
    state.locations = state.locations
      .filter(l => l.city !== loc.city)
  },

  [types.SET_SELL_PRICE_VALUE] (state, value) {
    state.price.currency_value = value
  },

  [types.SET_SELL_PRICE_CURRENCY] (state, cur) {
    state.price.currency_code = cur
  },

  [types.SET_SELL_OTHERS] (state, others) {
    if (others.hasOwnProperty('enable_questions')) {
      state.enable_questions = others.enable_questions
    }

    if (others.hasOwnProperty('auto_republication')) {
      state.auto_republication = others.auto_republication
    }

    if (others.hasOwnProperty('conditions')) {
      state.conditions = others.conditions
    }

    if (others.hasOwnProperty('type')) {
      state.type = others.type
    }
  },

  [types.SET_SELL_IMAGES] (state, images) {
    state.images = images && [...images] || []
  },

  [types.ADD_SELL_IMAGE] (state, image) {
    state.images = [...state.images, image]
  },

  [types.REMOVE_SELL_IMAGE] (state, id) {
    state.images = state.images.filter(i => i.id !== id)
  },

  [types.UPDATE_SELL_IMAGE] (state, image) {
    state.images = state.images.map(i => i.id === image.id ? image : i)
  },

  // to be removed
  // [types.APPEND_IMAGES] (state, images) {
  //   state.images = [...state.images, ...images]
  // },

  [types.SET_SELL_FETCHED_ATTRIBUTES] (state, attrs) {
    state.fetchedAttributes = Array.isArray(attrs) ? [...attrs] : []
  },

  [types.UPDATE_SELL_ATTRIBUTE] (state, attr) {
    state.fetchedAttributes = state.fetchedAttributes.map(a => a.id === attr.id ? attr : a)
  },

  [types.UPDATE_SELL_PHONE] (state, phone) {
    state.phones = state.phones
      .map(ph => ph.id !== phone.id ? ph : phone)
  },

  [types.REMOVE_SELL_PHONE] (state, {id, phone}) {
    state.phones = state.phones
      .filter(ph => ph.id !== id)
  },

  [types.SET_SELL_PUB_STATUS_ID] (state, status) {
    state.publication_status_id = status
  },

  [types.SET_SELL_PUB_STATUS] (state, status) {
    state.publication_status = status
  },

  [types.SET_HL_VALUE] (state, [key, value]) {
    state[key] = value
  },

  [types.SET_PAYMENT_GATEWAY] (state, gateway) {
    state.payment_gateway = gateway
  },

  [types.SET_HL_CALC] (state, data) {
    state.highlightsCalc = data
  },

  [types.SET_HL_FINAL] (state, data) {
    state.highlightsFinal = data
  },

  [types.SET_SELL_PHONES] (state, phones) {
    state.phones = phones || []
  },

  [types.SET_SELL_GEOPOINT] (state, geopoint) {
    state.geopoint = geopoint && {
      id: geopoint.id,
      city: geopoint.city,
      string: geopoint.string,
      lat: geopoint.coordinates.lat,
      lon: geopoint.coordinates.lon
    } || null
  },

  [types.SET_SELL_LOCATIONS] (state, locations) {
    state.locations = locations || []
  },

  [types.SET_SELL_EMAILS] (state, emails) {
    state.emails = emails || []
  },

  [types.SET_SELL_HL_REQUESTED] (state, value) {
    state.highlightRequested = value
  },

  [types.SET_SELL_CHECK_REQUIRED_FIELDS] (state, value) {
    state.checkRequiredFields = value
  },

  [types.SET_SELL_FIELDS_FILLED] (state, value) {
    state.fields_filled = value
  },

  [types.ADD_SELL_ATTR_WITH_ERRORS] (state, attr) {
    state.attrWithErrors = [...state.attrWithErrors, attr]
  },

  [types.REMOVE_SELL_ATTR_WITH_ERRORS] (state, attr) {
    state.attrWithErrors = state.attrWithErrors.filter(a => a !== attr)
  },

  [types.SET_SELL_STOCK] (state, value) {
    state.stock = value
  },

  [types.SET_SELL_SHIPPING_TYPE] (state, value) {
    state.shipping_type = value
  },

  [types.SET_CAT_MARKETABLE] (state, category_marketable) {
    state.category_marketable = category_marketable
  },

  [types.SET_SELL_FOR_SALE] (state, value) {
    state.forSale = value
  },

  [types.SET_SELL_LIMIT_ON_CATEGORY] (state, value) {
    state.limit_on_category = value
  },

  [types.SET_SELL_REMAINING_PUBS] (state, value) {
    state.remaining_publications = value
  },

  [types.SET_SELL_HIGHLIGHT_PENDING] (state, value) {
    state.highlight_pending = value
  },

  [types.SET_SELL_CATALOG_DOMAIN] (state, value) {
    state.catalogDomain = value
  },

  [types.SET_SELL_ATTRIBUTE_SUGGESTIONS] (state, {attributeId, suggestions}) {
    state.attributeSuggestions = suggestions && {
      domain: state.catalogDomain,
      attribute: attributeId,
      suggested: suggestions
    } || []
  },

  [types.ADD_SELL_ATTRIBUTE_SUGGESTIONS] (state, {attributeId, suggestions}) {
    const existingAttr = state.attributeSuggestions.filter(a => a.attribute === attributeId)
    if (existingAttr.length > 0) {
      existingAttr[0].suggested = suggestions
    } else {
      state.attributeSuggestions = suggestions && state.attributeSuggestions.concat({
        domain: state.catalogDomain,
        attribute: attributeId,
        suggested: suggestions
      }) || state.attributeSuggestions
    }
  },

  [types.REMOVE_SELL_ATTRIBUTE_SUGGESTIONS] (state, index) {
    state.attributeSuggestions[index].suggested = []
  },

  [types.SET_SELL_API_ERROR] (state, value) {
    state.apiError = value
  },

  [types.SET_SELL_IS_FETCHING] (state, value) {
    state.isFetching = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/sell.js